import { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import classnames from 'classnames'

import { Icon } from '~/components/atoms/Icon/Icon'
import LoadSpinner from '~/components/atoms/LoadSpinner/LoadSpinner'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useDebounceFn } from '~/hooks/useDebounceFn'

import styles from './SearchField.module.css'

export interface SearchFieldProps {
  isLoading?: boolean
  placeholder?: string
  className?: string
  value?: string
  testID?: string
  filterPosition: number | null
}

export default function SearchField({
  isLoading = false,
  placeholder,
  className,
  value,
  testID,
}: SearchFieldProps) {
  const [isOpen, setIsOpen] = useState(false)

  const input = useRef<HTMLInputElement>(null)
  const router = useRouter()

  const handleFocus = () => {
    if (input.current && !isOpen) {
      input.current.focus()
      setIsOpen(true)
    }
  }
  const [currentValue, setCurrentValue] = useState<string | undefined>(value)

  useEffect(() => {
    setIsOpen(false)
    if (input.current) input.current.blur()
    setCurrentValue('')
  }, [])

  const _pushBrowserUrl = (clear?: boolean) => {
    const pathname = router.pathname
    const pathnameArr = router.asPath.split('/').filter(Boolean)
    const isArtworkPage = router.asPath.indexOf('artists') !== -1

    const slug: any = router.query.slug || []
    const page = router.query.slug
      ? isNaN(parseInt(slug[0]))
        ? slug[0]
        : slug.reverse()[0]
      : router?.query?.slug

    const routerParams = {} as any
    const queryVal =
      input.current && input.current.value.length > 0
        ? input.current.value
        : undefined

    const routerQueryParams = queryVal
      ? { ...router.query, searchTerm: queryVal }
      : { ...router.query }
    if (clear || !queryVal) delete routerQueryParams?.searchTerm

    routerParams['query'] = routerQueryParams
    routerParams['pathname'] = router.pathname

    const path = routerQueryParams
      ? {
          pathname,
          query: { ...routerQueryParams },
        }
      : {
          pathname,
        }

    const artistPath = routerQueryParams
      ? {
          asPath: pathnameArr[0],
          query: { slug: page, ...routerQueryParams },
        }
      : {
          asPath: pathnameArr[0],
          query: { slug: page },
        }

    router.push(isArtworkPage ? path : artistPath, undefined, {
      shallow: true,
    })
  }
  const pushBrowserUrl = useDebounceFn(_pushBrowserUrl, 500)

  const handleValue = (val: string) => {
    setCurrentValue(val)
    // This might be the point to remove
    setIsOpen(!!val)
  }

  useEffect(() => {
    if (value) {
      handleValue(value)
    } else {
      setCurrentValue(value)
      if (input.current) {
        input?.current.blur()
        handleValue('')
      }
    }
  }, [value])

  useEffect(() => {
    const locationQuery = location?.search?.match(/searchTerm=([^&]*)/)
    const passedValue: string = locationQuery
      ? decodeURI(locationQuery[1])
      : (value as string)
    handleValue(passedValue)
  }, [])

  const handleChange = (e: any) => {
    const val = e.target.value
    pushBrowserUrl(!val)
  }

  const handleEnter = (e: any) => {
    if (e.key === 'Enter') e.target.blur()
  }

  return (
    <div
      className={classnames(
        styles.base,
        className && className,
        isOpen && styles.searchFocus,
        styles.searchOpen,
        currentValue && !isOpen && styles.searchWithValue
      )}
      data-testid={testID}
      id="searchautocomplete"
      onClick={handleFocus}
    >
      <Icon type="Magnify" width={15} height={15} responsive={false} />
      <div className={styles.searchField}>
        <input
          ref={input}
          type="text"
          onChange={handleChange}
          onKeyUp={handleEnter}
          className={classnames(styles.input, 'searchInput')}
          value={currentValue}
          onFocus={handleFocus}
          placeholder={placeholder}
        />

        {isLoading && (
          <div className={styles.loadSpinner}>
            <LoadSpinner />
          </div>
        )}
        {placeholder && !input?.current?.value && (
          <Text
            size={TextSizes.bodyMed}
            Tag="label"
            className={classnames(styles.label, {
              // [styles.notEmpty]: isOpen,
            })}
          >
            {placeholder}
          </Text>
        )}
      </div>
    </div>
  )
}
